import { useState } from "react";
import "./dashboard.css";
import { TimeRangeControl, KpiFilterPopover } from "./dashboard-header";
import { KpiCards } from "./kpi-cards";
import { AuditPipeline } from "./audit-pipeline";
import { FindingsSeverity } from "./findings-severity";
import { FrameworkCoverage } from "./framework-coverage";
import { ExpiryWarnings } from "./expiry-warnings";
import { TeamActivity } from "./team-activity";
import { CloudUpload, Plus, FileText, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { Button } from "@/components/ui/primitives";
import type { TimeRange, KpiId } from "./dashboard.data";
import { DEFAULT_VISIBLE_KPIS, ALL_KPI_IDS } from "./dashboard.data";

const STORAGE_KEY = "dashboard:visibleKpis";
const ORDER_KEY = "dashboard:kpiOrder";

function loadVisibleKpis(): KpiId[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as KpiId[];
  } catch {
    // ignore parse errors
  }
  return DEFAULT_VISIBLE_KPIS;
}

function loadKpiOrder(): KpiId[] {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as KpiId[];
      // Validate: every known KPI must be present in the stored order
      if (ALL_KPI_IDS.every((id) => parsed.includes(id))) {
        return parsed;
      }
    }
  } catch {
    // ignore parse errors
  }
  return [...ALL_KPI_IDS];
}

const cardAnimation = (index: number) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { ...springs.smooth, delay: index * 0.04 },
});

export function DashboardPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>("monthly");
  const [visibleKpis, setVisibleKpis] = useState<KpiId[]>(loadVisibleKpis);
  const [kpiOrder, setKpiOrder] = useState<KpiId[]>(loadKpiOrder);

  const handleVisibleKpisChange = (newVisible: KpiId[]) => {
    const added = newVisible.filter((id) => !visibleKpis.includes(id));

    let nextOrder = kpiOrder;
    if (added.length > 0) {
      // Newly enabled cards go to end of order
      nextOrder = [...kpiOrder.filter((id) => !added.includes(id)), ...added];
      setKpiOrder(nextOrder);
      localStorage.setItem(ORDER_KEY, JSON.stringify(nextOrder));
    }

    setVisibleKpis(newVisible);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newVisible));
  };

  const handleResetOrder = () => {
    const defaultOrder = [...ALL_KPI_IDS];
    setKpiOrder(defaultOrder);
    localStorage.setItem(ORDER_KEY, JSON.stringify(defaultOrder));
  };

  const handleKpiOrderChange = (newOrder: KpiId[]) => {
    setKpiOrder(newOrder);
    localStorage.setItem(ORDER_KEY, JSON.stringify(newOrder));
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="p-6 w-full">
        <motion.div
          className="flex items-center justify-between mb-4"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springs.smooth}
        >
          <h1 className="text-[24px] font-bold text-primary">Dashboard</h1>
          <div className="flex items-center gap-2">
            <Button variant="secondary" icon={CloudUpload}>
              Upload
            </Button>
            <Button variant="secondary" icon={AlertTriangle}>
              Minding melden
            </Button>
            <Button variant="secondary" icon={FileText}>
              Report erstellen
            </Button>
            <Button variant="primary" icon={Plus}>
              Neues Audit
            </Button>
          </div>
        </motion.div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-primary">Overview</h2>
            <div className="flex items-center gap-2">
              <TimeRangeControl
                timeRange={timeRange}
                onTimeRangeChange={setTimeRange}
              />
              <KpiFilterPopover
                visibleKpis={visibleKpis}
                onVisibleKpisChange={handleVisibleKpisChange}
                onResetOrder={handleResetOrder}
              />
            </div>
          </div>
          <KpiCards
            timeRange={timeRange}
            visibleKpis={visibleKpis}
            kpiOrder={kpiOrder}
            onKpiOrderChange={handleKpiOrderChange}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <motion.div {...cardAnimation(0)} className="h-[340px]">
            <AuditPipeline />
          </motion.div>
          <motion.div {...cardAnimation(1)} className="h-[340px]">
            <FrameworkCoverage />
          </motion.div>
          <motion.div {...cardAnimation(2)} className="h-[340px]">
            <ExpiryWarnings />
          </motion.div>
          <motion.div {...cardAnimation(3)} className="md:col-span-2 h-[340px]">
            <FindingsSeverity />
          </motion.div>
          <motion.div {...cardAnimation(4)}>
            <TeamActivity />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
